import React, { useState } from 'react';
import { urlMap, getAuthHeaders } from '../../../utils';

const COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'selected', label: 'Selected for Work' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'testing', label: 'Testing' },
  { key: 'done', label: 'Done' },
];

const SEVERITY_COLOR = {
  critical: '#b91c1c',
  high: '#d97706',
  medium: '#2563eb',
  low: '#6b7280',
};

const SEVERITY_LABEL = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SCOPE_BADGE_COLOR = {
  project: '#8b5cf6',
  chapter: '#3b82f6',
};

function getDisplayTitle(issue) {
  const isProjectLevel = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
  if (isProjectLevel && /^project issue$/i.test(String(issue.title || '').trim())) {
    const alt = issue.description || issue.suggestion || issue.issue_type || '';
    const normalized = String(alt).replace(/\s+/g, ' ').trim();
    return normalized.length <= 72 ? normalized : `${normalized.slice(0, 72).trim()}…`;
  }
  return issue.title || 'Untitled issue';
}

/**
 * ChapterIssueKanbanCard — kanban card for chapter_issues table.
 *
 * Props:
 *  - issue: object
 *  - projectId: string
 *  - isDragging: boolean
 *  - onDragStart: (e) => void
 *  - onDragEnd: () => void
 *  - onUpdated: (updated) => void   — called after move/save with updated issue
 *  - onDeleted: (issueId) => void   — called after delete
 *  - onDetailClick: (issue) => void  — opens detail overlay
 */
export default function ChapterIssueKanbanCard({ issue, projectId, isDragging, onDragStart, onDragEnd, onUpdated, onDeleted, onDetailClick, onMoveToChapter }) {
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);

  const color = SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.medium;
  const isProjectLevel = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
  const scopeLabel = isProjectLevel ? 'Project' : `Ch ${issue.chapter_number}`;
  const scopeColor = isProjectLevel ? SCOPE_BADGE_COLOR.project : SCOPE_BADGE_COLOR.chapter;

  const moveIssue = async (newStatus) => {
    setMoveMenuOpen(false);
    if (newStatus === issue.status) return;
    // Optimistic
    onUpdated({ ...issue, status: newStatus });
    try {
      const res = await fetch(urlMap.updateProjectIssueStatus(projectId, issue.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      const updated = await res.json();
      onUpdated({ ...updated, _source: 'chapter' });
    } catch {
      onUpdated(issue); // revert
    }
  };

  const saveIssue = async ({ title, description, severity }) => {
    const issueUpdateUrl = issue.chapter_id
      ? urlMap.updateChapterIssue(issue.chapter_id, issue.id)
      : urlMap.updateProjectIssue(projectId, issue.id);
    const res = await fetch(issueUpdateUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ title, description, severity }),
    });
    if (!res.ok) throw new Error('Save failed');
    const updated = await res.json();
    onUpdated({ ...updated, _source: 'chapter' });
  };

  const deleteIssue = async () => {
    const ok = window.confirm('Delete this issue? This cannot be undone.');
    if (!ok) return;
    const issueDeleteUrl = issue.chapter_id
      ? urlMap.deleteChapterIssue(issue.chapter_id, issue.id)
      : urlMap.deleteProjectIssue(projectId, issue.id);
    const res = await fetch(issueDeleteUrl, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Delete failed');
    onDeleted(issue.id);
  };

  return (
    <div
      className={`kanban-card${isDragging ? ' dragging' : ''}${issue.status === 'done' ? ' kanban-card--done' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onDetailClick?.({ ...issue, _moveIssue: moveIssue, _saveIssue: saveIssue, _deleteIssue: deleteIssue, _openMoveToChapter: () => onMoveToChapter?.(issue) })}
    >
      <div className="kanban-card-top">
        <span className="kanban-card-severity" style={{ background: color }} />
        <span className="kanban-card-severity-label" style={{ color }}>
          {SEVERITY_LABEL[issue.severity] || issue.severity}
        </span>
        <span className="kanban-card-scope" style={{ background: `${scopeColor}18`, color: scopeColor }}>{scopeLabel}</span>
        <span style={{ flex: 1 }} />
        <span className="kanban-card-move" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="kanban-card-move-btn"
            title="Move to column"
            onClick={(e) => { e.stopPropagation(); setMoveMenuOpen((v) => !v); }}
          >
            ⋮
          </button>
          {moveMenuOpen && (
            <div className="kanban-card-move-menu">
              {COLUMNS.map((col) => (
                <button
                  key={col.key}
                  type="button"
                  className={`kanban-card-move-item${col.key === issue.status ? ' kanban-card-move-item--current' : ''}`}
                  onClick={(e) => { e.stopPropagation(); moveIssue(col.key); }}
                >
                  {col.key === issue.status ? `● ${col.label}` : col.label}
                </button>
              ))}
              {onMoveToChapter && (
                <>
                  <div className="kanban-card-move-divider" />
                  <button
                    type="button"
                    className="kanban-card-move-item kanban-card-move-item--chapter"
                    onClick={(e) => { e.stopPropagation(); setMoveMenuOpen(false); onMoveToChapter(issue); }}
                  >
                    ↗ Move to Chapter…
                  </button>
                </>
              )}
            </div>
          )}
        </span>
      </div>
      <div className="kanban-card-title">{getDisplayTitle(issue)}</div>
      {issue.issue_type && (
        <div className="kanban-card-footer">
          <span className="kanban-card-type">{String(issue.issue_type).replace(/_/g, ' ')}</span>
        </div>
      )}
    </div>
  );
}
